import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMyProfile } from '@/features/profile';
import { Async } from '@/shared/ui/Async';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { TextArea } from '@/shared/ui/TextArea';
import { useSessionStore } from '@/stores/session.store';

import {
  useAddInternalNote,
  useAdmins,
  useAssignTicket,
  useReopenTicket,
  useReplyTicket,
  useSetPriority,
  useTicket,
  useTicketMessages,
  useUpdateTicketStatus,
} from '../hooks/use-support';
import {
  isTerminal,
  SUPPORT_TICKET_PRIORITIES,
  SUPPORT_TICKET_STATUSES,
  type SupportTicket,
  type SupportTicketPriority,
  type SupportTicketStatus,
} from '../model/support-ticket';

export function priorityColor(priority: SupportTicketPriority): string {
  switch (priority) {
    case 'urgent':
      return 'var(--color-danger)';
    case 'high':
      return 'var(--color-accent-text)';
    case 'low':
      return 'var(--color-on-surface-muted)';
    default:
      return 'var(--color-primary)';
  }
}

export function statusColor(status: SupportTicketStatus): string {
  switch (status) {
    case 'open':
    case 'waiting_on_user':
      return 'var(--color-accent-text)';
    case 'in_progress':
      return 'var(--color-primary)';
    case 'resolved':
    case 'closed':
      return 'var(--color-success)';
  }
}

interface TicketDetailProps {
  ticketId: string;
}

export function TicketDetail({ ticketId }: TicketDetailProps): JSX.Element {
  const query = useTicket(ticketId);
  return <Async query={query}>{(t) => <TicketBody ticket={t} />}</Async>;
}

function TicketBody({ ticket }: { ticket: SupportTicket }): JSX.Element {
  const { t } = useTranslation();
  const me = useMyProfile();
  const isAdmin = me.data?.isAdmin ?? false;
  const isCreator = useSessionStore((s) => s.userId) === ticket.createdBy;
  const canWrite = !isTerminal(ticket.status) && (isAdmin || isCreator);

  return (
    <div className="stack">
      <section className="card stack">
        <div className="row row--between">
          <h2>{ticket.subject}</h2>
          <div className="wrap">
            <Badge
              label={t(`support.status_${ticket.status}`)}
              color={statusColor(ticket.status)}
            />
            <Badge
              label={t(`support.priority_${ticket.priority}`)}
              color={priorityColor(ticket.priority)}
            />
          </div>
        </div>
        <div className="wrap">
          {ticket.createdByName ? (
            <span className="muted">{t('support.filedBy', { name: ticket.createdByName })}</span>
          ) : null}
          <Badge
            label={t(`support.category_${ticket.category}`)}
            color="var(--color-on-surface-muted)"
          />
          <Badge
            label={t(`support.role_${ticket.roleAtCreation}`)}
            color="var(--color-on-surface-muted)"
          />
          {ticket.assignedAdminName ? (
            <span className="muted">
              {t('support.assignedTo', { name: ticket.assignedAdminName })}
            </span>
          ) : null}
        </div>
      </section>

      {isTerminal(ticket.status) && (isAdmin || isCreator) ? (
        <ReopenSection ticketId={ticket.id} />
      ) : null}

      {isAdmin ? <AdminActions ticket={ticket} /> : null}

      <ThreadSection ticketId={ticket.id} />

      {canWrite ? (
        <ReplyBox ticketId={ticket.id} />
      ) : (
        <p className="muted">{t('support.threadClosed')}</p>
      )}
    </div>
  );
}

function ReopenSection({ ticketId }: { ticketId: string }): JSX.Element {
  const { t } = useTranslation();
  const reopen = useReopenTicket(ticketId);
  return (
    <section className="card row row--between">
      <p className="muted">{t('support.reopenHint')}</p>
      <Button variant="outline" loading={reopen.isPending} onClick={() => reopen.mutate()}>
        {t('support.reopen')}
      </Button>
    </section>
  );
}

function ThreadSection({ ticketId }: { ticketId: string }): JSX.Element {
  const { t } = useTranslation();
  const messages = useTicketMessages(ticketId);
  return (
    <Async query={messages} isEmpty={(l) => l.length === 0} emptyMessage={t('support.noMessages')}>
      {(list) => (
        <ul className="stack" style={{ listStyle: 'none', padding: 0 }}>
          {list.map((m) => (
            <li key={m.id} className={`card stack${m.isInternal ? ' card--internal-note' : ''}`}>
              <div className="row row--between">
                <div className="row wrap" style={{ gap: 'var(--space-2)' }}>
                  <strong>
                    {m.senderId === null ? t('support.systemAuthor') : (m.senderName ?? '—')}
                  </strong>
                  {m.isInternal ? (
                    <Badge label={t('support.internalBadge')} color="var(--color-accent-text)" />
                  ) : null}
                </div>
                <span className="muted">{new Date(m.createdAt).toLocaleString()}</span>
              </div>
              <p style={{ whiteSpace: 'pre-wrap' }}>{m.body}</p>
            </li>
          ))}
        </ul>
      )}
    </Async>
  );
}

function ReplyBox({ ticketId }: { ticketId: string }): JSX.Element {
  const { t } = useTranslation();
  const reply = useReplyTicket(ticketId);
  const [body, setBody] = useState('');

  function submit() {
    const trimmed = body.trim();
    if (trimmed.length === 0) return;
    reply.mutate(trimmed, { onSuccess: () => setBody('') });
  }

  return (
    <section className="card stack">
      <TextArea
        label={t('support.replyLabel')}
        placeholder={t('support.replyPlaceholder')}
        value={body}
        maxLength={4000}
        onChange={(e) => setBody(e.target.value)}
      />
      <Button loading={reply.isPending} onClick={submit}>
        {t('support.send')}
      </Button>
    </section>
  );
}

function AdminActions({ ticket }: { ticket: SupportTicket }): JSX.Element {
  const { t } = useTranslation();
  const userId = useSessionStore((s) => s.userId);
  const admins = useAdmins();
  const assign = useAssignTicket(ticket.id);
  const setPriority = useSetPriority(ticket.id);
  const setStatus = useUpdateTicketStatus(ticket.id);
  const addNote = useAddInternalNote(ticket.id);
  const [note, setNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [pendingStatus, setPendingStatus] = useState<SupportTicketStatus>(ticket.status);

  return (
    <section className="card stack">
      <h3>{t('support.adminControls')}</h3>

      <div className="wrap">
        {ticket.assignedAdminId === userId ? (
          <Button variant="outline" loading={assign.isPending} onClick={() => assign.mutate(null)}>
            {t('support.unassign')}
          </Button>
        ) : (
          <Button
            variant="outline"
            loading={assign.isPending}
            onClick={() => assign.mutate(userId ?? null)}
          >
            {t('support.assignToMe')}
          </Button>
        )}
      </div>

      <label className="field">
        <span className="field__label">{t('support.assigneeLabel')}</span>
        <select
          className="field__input"
          disabled={admins.isPending || assign.isPending}
          value={ticket.assignedAdminId ?? ''}
          onChange={(e) => assign.mutate(e.target.value.length === 0 ? null : e.target.value)}
        >
          <option value="">{t('support.assigneeUnassigned')}</option>
          {(admins.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.fullName}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        <span className="field__label">{t('support.priority')}</span>
        <select
          className="field__input"
          disabled={setPriority.isPending}
          value={ticket.priority}
          onChange={(e) => setPriority.mutate(e.target.value as SupportTicketPriority)}
        >
          {SUPPORT_TICKET_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {t(`support.priority_${p}`)}
            </option>
          ))}
        </select>
      </label>

      <div className="row">
        <label className="field" style={{ flex: 1 }}>
          <span className="field__label">{t('support.setStatus')}</span>
          <select
            className="field__input"
            value={pendingStatus}
            onChange={(e) => setPendingStatus(e.target.value as SupportTicketStatus)}
          >
            {SUPPORT_TICKET_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`support.status_${s}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <TextArea
        label={t('support.transitionNote')}
        placeholder={t('support.transitionNoteHint')}
        value={note}
        maxLength={4000}
        onChange={(e) => setNote(e.target.value)}
      />
      <Button
        loading={setStatus.isPending}
        onClick={() =>
          setStatus.mutate(
            { status: pendingStatus, note: note.trim().length === 0 ? null : note.trim() },
            { onSuccess: () => setNote('') },
          )
        }
      >
        {t('support.applyStatus')}
      </Button>

      <div
        className="stack"
        style={{ borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-3)' }}
      >
        <h4>{t('support.internalNoteSectionTitle')}</h4>
        <TextArea
          label={t('support.internalNoteLabel')}
          placeholder={t('support.internalNotePlaceholder')}
          value={internalNote}
          maxLength={4000}
          onChange={(e) => setInternalNote(e.target.value)}
        />
        <Button
          variant="outline"
          loading={addNote.isPending}
          onClick={() => {
            const trimmed = internalNote.trim();
            if (trimmed.length === 0) return;
            addNote.mutate(trimmed, { onSuccess: () => setInternalNote('') });
          }}
        >
          {t('support.addInternalNote')}
        </Button>
      </div>
    </section>
  );
}
